import React, { useMemo, useState } from "react";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { titleCase } from "./format";

export const EMPTY_FILTERS = {
  state: "",
  region: "",
  zone: "",
  parent_client: "",
  client: "",
  designation: "",
  shift: "",
};

const FILTER_LABELS = {
  state: "State",
  region: "Region",
  zone: "Zone",
  parent_client: "Client",
  client: "Location",
  designation: "Designation",
  shift: "Shift",
};

// Geography filters below the changed one no longer make sense
const CHILDREN = {
  state: ["region", "zone", "client"],
  region: ["zone", "client"],
  zone: ["client"],
  parent_client: ["client"],
};

const uniqueById = (rows) => {
  const seen = new Set();
  return rows.filter((r) => (seen.has(r.id) ? false : seen.add(r.id)));
};

export const withChildrenCleared = (filters, key, value) => {
  const next = { ...filters, [key]: value };
  (CHILDREN[key] || []).forEach((child) => (next[child] = ""));
  return next;
};

const Select = ({ label, value, onChange, options, allLabel }) => (
  <div className="a2-field">
    <label>{label}</label>
    <select className="a2-select" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{allLabel}</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </select>
  </div>
);

const FilterPanel = ({
  reportDate,
  onReportDate,
  draft,
  setDraft,
  applied,
  options,
  onApply,
  onClear,
  onReset,
  onSave,
  onRemoveApplied,
}) => {
  const [open, setOpen] = useState(true);
  const [more, setMore] = useState(false);

  const lists = useMemo(() => {
    const o = options || {};
    const match = (row, keys) => keys.every((k) => !draft[k] || row[k] === draft[k]);
    return {
      states: o.states || [],
      regions: uniqueById((o.regions || []).filter((r) => match(r, ["state"]))),
      zones: uniqueById((o.zones || []).filter((r) => match(r, ["state", "region"]))),
      clients: o.clients || [],
      locations: uniqueById(
        (o.locations || []).filter((r) => match(r, ["state", "region", "zone", "parent_client"]))
      ),
      designations: o.designations || [],
      shifts: (o.shifts || []).map((s) => ({ id: s, name: s })),
    };
  }, [options, draft]);

  const set = (key) => (value) => setDraft(withChildrenCleared(draft, key, value));
  const activeCount = Object.values(applied).filter(Boolean).length;
  const dirty = JSON.stringify(draft) !== JSON.stringify(applied);

  const nameOf = (key, id) => {
    const source = {
      state: lists.states,
      region: options?.regions,
      zone: options?.zones,
      parent_client: lists.clients,
      client: options?.locations,
    }[key];
    return (source || []).find((o) => o.id === id)?.name || titleCase(id);
  };

  return (
    <div className="a2-card a2-noprint">
      <div className="a2-filter-head">
        <h3>
          <FilterAltOutlinedIcon fontSize="small" className="t-indigo" /> Filters
        </h3>
        <span className="a2-badge">{activeCount} active</span>
        <button className="a2-btn a2-btn-ghost" onClick={() => setOpen(!open)}>
          {open ? "Hide ▴" : "Show ▾"}
        </button>
      </div>

      {open && (
        <>
          <div className="a2-filter-grid">
            <div className="a2-field">
              <label>Date</label>
              <input
                type="date"
                className="a2-input"
                value={reportDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => e.target.value && onReportDate(e.target.value)}
              />
            </div>
            <Select label="State" value={draft.state} onChange={set("state")} options={lists.states} allLabel="All states" />
            <Select label="Region" value={draft.region} onChange={set("region")} options={lists.regions} allLabel="All regions" />
            <Select label="Zone" value={draft.zone} onChange={set("zone")} options={lists.zones} allLabel="All zones" />
            <Select label="Client" value={draft.parent_client} onChange={set("parent_client")} options={lists.clients} allLabel="All clients" />
            {more && (
              <>
                <Select label="Location" value={draft.client} onChange={set("client")} options={lists.locations} allLabel="All locations" />
                <Select label="Designation" value={draft.designation} onChange={set("designation")} options={lists.designations} allLabel="All designations" />
                <Select label="Shift" value={draft.shift} onChange={set("shift")} options={lists.shifts} allLabel="All shifts" />
              </>
            )}
          </div>

          <div className="a2-filter-foot">
            <button className="a2-btn a2-btn-link" onClick={() => setMore(!more)}>
              {more ? "Fewer filters ▴" : "More filters ▾"}
            </button>
            <div className="a2-filter-foot-right">
              <button className="a2-btn a2-btn-ghost" onClick={onClear}>
                Clear All
              </button>
              <button className="a2-btn" onClick={onSave}>Save Filter</button>
              <button className="a2-btn" onClick={onReset}>Reset Filters</button>
              <button className="a2-btn a2-btn-primary" disabled={!dirty} onClick={onApply}>
                Apply Filters
              </button>
            </div>
          </div>
        </>
      )}

      {activeCount > 0 && (
        <div className="a2-chips">
          Applied:
          {Object.entries(applied)
            .filter(([, v]) => v)
            .map(([k, v]) => (
              <span className="a2-chip" key={k}>
                {FILTER_LABELS[k]}: {nameOf(k, v)}
                <button aria-label={`Remove ${FILTER_LABELS[k]}`} onClick={() => onRemoveApplied(k)}>
                  ×
                </button>
              </span>
            ))}
        </div>
      )}
    </div>
  );
};

export default FilterPanel;
